import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert, Button, Form, Input, Modal, Popconfirm, Select, Space, Switch, Table, Tag, message,
} from 'antd';
import { PlusOutlined, ReloadOutlined } from '@ant-design/icons';
import { testDocAPI } from '../../services/api';
import { DatasetRow, SiteTag } from './common';
import { gray, font } from '../../theme/tokens';

/* 테스트 데이터 명세 탭
   **비밀번호 같은 값은 저장하지 않는다** — '민감' 으로 표시하면 값은 버리고 출처만 남긴다.
   문서(PDF)에 그대로 실리기 때문이다. */

interface Props { bodyHeight: number; canWrite: boolean; canDelete: boolean; }

const TYPE_LABEL: Record<string, string> = {
  account: '계정', dynamic: '동적 선택', param: '파라미터', env: '환경변수',
};

const DatasetTab: React.FC<Props> = ({ bodyHeight, canWrite, canDelete }) => {
  const [rows, setRows] = useState<DatasetRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [edit, setEdit] = useState<DatasetRow | null>(null);
  const [open, setOpen] = useState(false);
  const [form] = Form.useForm();
  const sensitive = Form.useWatch('is_sensitive', form);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await testDocAPI.datasets();
      setRows(res.data.items || []);
    } catch (e: any) {
      message.error(e?.response?.data?.detail || '데이터 명세를 불러오지 못했습니다.');
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const doSave = async () => {
    try {
      const v = await form.validateFields();
      if (edit) await testDocAPI.updateDataset(edit.id, v);
      else await testDocAPI.createDataset(v);
      message.success(edit ? '수정되었습니다.' : '등록되었습니다.');
      setOpen(false); setEdit(null); form.resetFields();
      load();
    } catch (e: any) {
      if (e?.errorFields) return;
      message.error(e?.response?.data?.detail || '저장에 실패했습니다.');
    }
  };

  const columns = [
    { title: '코드', dataIndex: 'code', width: 175,
      render: (v: string) => <span style={{ fontFamily: font.mono, fontSize: 12, fontWeight: 600 }}>{v}</span> },
    { title: '구분', dataIndex: 'data_type', width: 100,
      render: (v: string) => <Tag style={{ margin: 0 }}>{TYPE_LABEL[v] || v}</Tag> },
    { title: '사이트', dataIndex: 'site', width: 85,
      render: (v: string) => (v ? <SiteTag site={v} /> : <span style={{ color: gray[300] }}>공용</span>) },
    { title: '이름', dataIndex: 'name', width: 190 },
    { title: '값 · 규칙', dataIndex: 'value', ellipsis: true,
      render: (v: string, r: DatasetRow) => (r.is_sensitive
        ? <Tag color="red" style={{ margin: 0 }}>민감정보 — 값 미보관</Tag>
        : <span style={{ fontSize: 12, color: gray[600] }}>{v || '—'}</span>) },
    { title: '출처', dataIndex: 'source', ellipsis: true,
      render: (v: string) => <span style={{ fontFamily: font.mono, fontSize: 11.5, color: gray[500] }}>{v || '—'}</span> },
    ...(canWrite || canDelete ? [{
      title: '', width: 110, fixed: 'right' as const,
      render: (_: any, r: DatasetRow) => (
        <Space size={4}>
          {canWrite && <Button size="small" onClick={() => {
            setEdit(r); form.setFieldsValue(r); setOpen(true);
          }}>수정</Button>}
          {canDelete && (
            <Popconfirm title="이 데이터 명세를 삭제할까요?" okText="삭제" cancelText="취소"
              onConfirm={async () => {
                try { await testDocAPI.removeDataset(r.id); message.success('삭제되었습니다.'); load(); }
                catch { message.error('삭제에 실패했습니다.'); }
              }}>
              <Button size="small" danger type="text">삭제</Button>
            </Popconfirm>
          )}
        </Space>
      ),
    }] : []),
  ];

  return (
    <>
      <div style={{ display: 'flex', gap: 8, marginBottom: 10 }}>
        {canWrite && (
          <Button type="primary" icon={<PlusOutlined />} onClick={() => {
            setEdit(null);
            form.resetFields();
            form.setFieldsValue({ data_type: 'param', is_sensitive: false });
            setOpen(true);
          }}>데이터 등록</Button>
        )}
        <Button icon={<ReloadOutlined />} onClick={load} />
        <span style={{ marginLeft: 'auto', fontSize: 12, color: gray[400], alignSelf: 'center' }}>
          실행마다 실제로 쓴 값은 각 실행의 결과보고서에 기록됩니다
        </span>
      </div>

      <Alert type="info" showIcon style={{ marginBottom: 10 }}
        message="비밀번호·토큰은 등록하지 마세요."
        description="'민감정보' 로 표시하면 값은 저장하지 않고 출처(환경변수명)만 남깁니다. 이 명세는 계획서 PDF에 그대로 실립니다." />

      <Table<DatasetRow>
        rowKey="id" size="small" loading={loading} dataSource={rows} columns={columns as any}
        scroll={{ y: bodyHeight, x: 1100 }}
        rowClassName={(_, i) => (i % 2 ? 'table-row-alt' : '')}
        pagination={{
          pageSize: 20, showSizeChanger: true, pageSizeOptions: ['20', '50', '100', '200'],
          showTotal: (t, r) => `${r[0]}–${r[1]} / 총 ${t}건`, size: 'small',
        }}
      />

      <Modal open={open} onCancel={() => { setOpen(false); setEdit(null); }} onOk={doSave}
        okText="저장" cancelText="취소" width={620}
        title={edit ? '테스트 데이터 수정' : '테스트 데이터 등록'}>
        <Form form={form} layout="vertical" size="small" style={{ marginTop: 8 }}>
          <Space size={12}>
            <Form.Item name="code" label="코드" rules={[{ required: true, message: '코드를 입력하세요' }]}
              style={{ width: 200 }}>
              <Input placeholder="LAB_ACCOUNT" />
            </Form.Item>
            <Form.Item name="data_type" label="구분" style={{ width: 140 }}>
              <Select options={Object.entries(TYPE_LABEL).map(([v, l]) => ({ value: v, label: l }))} />
            </Form.Item>
            <Form.Item name="site" label="사이트" style={{ width: 150 }}>
              <Select allowClear placeholder="공용" options={[
                { value: 'lab', label: 'lab' }, { value: 'partner', label: 'partner' }]} />
            </Form.Item>
          </Space>
          <Form.Item name="name" label="이름"><Input placeholder="lab 관리자 테스트 계정" /></Form.Item>
          <Form.Item name="is_sensitive" label="민감정보 (값을 저장하지 않음)" valuePropName="checked">
            <Switch />
          </Form.Item>
          <Form.Item name="value" label="값 · 선택 규칙">
            <Input.TextArea rows={2} disabled={!!sensitive}
              placeholder={sensitive ? '민감정보로 표시되어 값은 저장되지 않습니다' : 'admin / 첫 행의 시험일정 ID 등'} />
          </Form.Item>
          <Form.Item name="source" label="출처">
            <Input placeholder="UITEST_LAB_USER 환경변수 / GET /api/partner/schedules?page_size=1" />
          </Form.Item>
          <Form.Item name="note" label="비고"><Input.TextArea rows={2} /></Form.Item>
        </Form>
      </Modal>
    </>
  );
};

export default DatasetTab;
